import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const ThemeContext = createContext({ theme: "light", manual: false, toggle: () => {}, reset: () => {} });

// Houston time is the visitor's local time per spec.
// 6:00–18:59 -> light, 19:00–05:59 -> dark
function getAutoTheme(d = new Date()) {
  const h = d.getHours();
  return h >= 6 && h < 19 ? "light" : "dark";
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") return "light";
    const saved = localStorage.getItem("houstoac_theme_override");
    if (saved === "light" || saved === "dark") return saved;
    return getAutoTheme();
  });
  const [manual, setManual] = useState(() => {
    if (typeof window === "undefined") return false;
    return !!localStorage.getItem("houstoac_theme_override");
  });

  // Apply class to <html>
  useEffect(() => {
    const root = document.documentElement;
    root.classList.add("theme-transition");
    if (theme === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
    const t = setTimeout(() => root.classList.remove("theme-transition"), 700);
    return () => clearTimeout(t);
  }, [theme]);

  // Auto-update every minute when not in manual mode
  useEffect(() => {
    if (manual) return;
    const tick = () => setTheme(getAutoTheme());
    tick();
    const id = setInterval(tick, 60_000);
    return () => clearInterval(id);
  }, [manual]);

  const toggle = useCallback(() => {
    setTheme((prev) => {
      const next = prev === "light" ? "dark" : "light";
      localStorage.setItem("houstoac_theme_override", next);
      return next;
    });
    setManual(true);
  }, []);

  const reset = useCallback(() => {
    localStorage.removeItem("houstoac_theme_override");
    setManual(false);
    setTheme(getAutoTheme());
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, manual, toggle, reset }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
